/**
 * SmartScorecard.jsx
 *
 * Usage:
 *   import SmartScorecard from './SmartScorecard';
 *
 *   // From a parsed event object
 *   <SmartScorecard data={smartScoreEvent} />
 *
 *   // Or pass a raw JSON string
 *   <SmartScorecard json={rawJsonString} />
 *
 * Props:
 *   data  {object}  - Parsed SmartScore event object
 *   json  {string}  - Raw JSON string (alternative to data)
 *
 * Requires: React 17+, Tailwind CSS (or swap inline styles below)
 */

import React from 'react';

// ---------------------------------------------------------------------------
// Sub-score config — add/remove rows here as the API evolves.
// warn: true forces amber colour regardless of score value.
// ---------------------------------------------------------------------------
const SUB_SCORE_CONFIG = [
	{ key: 'job_function_alignment', label: 'Job function', warn: false },
	{ key: 'role_seniority_alignment', label: 'Seniority', warn: false },
	{ key: 'industry_domain_relevance', label: 'Industry / domain', warn: false },
	{ key: 'education_requirements_alignment', label: 'Education', warn: false },
	{ key: 'certifications_alignment', label: 'Certifications', warn: false },
	{ key: 'other_mandatory_qualifications', label: 'Mandatory qualifications', warn: false },
	{ key: 'skills_alignment', label: 'Skills alignment', warn: true },
	{ key: 'skills_deficiencies', label: 'Skills deficiencies', warn: true },
	{ key: 'company_scale_alignment', label: 'Company scale', warn: false },
];

// ---------------------------------------------------------------------------
// Theme helpers
// ---------------------------------------------------------------------------
function getScoreTheme(score, warn) {
	if (warn || score <= 4) {
		return { bg: '#fff3cd', fg: '#7d4e00', label: score <= 4 ? 'Poor' : 'Review' };
	}
	if (score <= 6) return { bg: '#fff3cd', fg: '#7d4e00', label: 'Review' };
	if (score <= 7) return { bg: '#d1ecf1', fg: '#0c5460', label: 'Good' };
	return { bg: '#d4edda', fg: '#155724', label: score === 10 ? 'Perfect' : 'Excellent' };
}

function getSummaryBorderColor(score) {
	if (score <= 6) return '#f0ad4e';
	if (score <= 7) return '#17a2b8';
	return '#28a745';
}

function formatDate(isoString) {
	if (!isoString) return '';
	return new Date(isoString).toLocaleDateString('en-GB', {
		day: 'numeric', month: 'short', year: 'numeric',
	});
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function Badge({ bg, fg, children, small = false }) {
	return (
		<span
			style={{
				fontSize: small ? 10 : 11,
				padding: small ? '2px 7px' : '3px 10px',
				borderRadius: 20,
				fontWeight: 500,
				whiteSpace: 'nowrap',
				background: bg,
				color: fg,
			}}
		>
			{children}
		</span>
	);
}

function ScoreTile({ label, score, warn }) {
	const theme = getScoreTheme(score, warn);
	return (
		<div
			style={{
				background: 'var(--color-background-primary, #fff)',
				border: '0.5px solid var(--color-border-tertiary, #e2e5ea)',
				borderRadius: 8,
				padding: '10px 12px',
			}}
		>
			<p style={{ margin: '0 0 4px', fontSize: 11, color: 'var(--color-text-secondary, #6b7280)' }}>
				{label}
			</p>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<span style={{ fontSize: 18, fontWeight: 500, color: theme.fg }}>{score}</span>
				<Badge bg={theme.bg} fg={theme.fg} small>{theme.label}</Badge>
			</div>
			<div
				style={{
					height: 4,
					background: 'var(--color-background-secondary, #f3f4f6)',
					borderRadius: 2,
					marginTop: 6,
					overflow: 'hidden',
				}}
			>
				<div
					style={{
						height: '100%',
						width: `${score * 10}%`,
						background: theme.fg,
						borderRadius: 2,
						transition: 'width 0.4s ease',
					}}
				/>
			</div>
		</div>
	);
}

function DetailRow({ label, score, text, warn, isLast }) {
	const theme = getScoreTheme(score, warn);
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				gap: 12,
				padding: '12px 14px',
				borderBottom: isLast ? 'none' : '0.5px solid var(--color-border-tertiary, #e2e5ea)',
			}}
		>
			<div style={{ flex: 1 }}>
				<p style={{ margin: '0 0 3px', fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary, #111)' }}>
					{label}
				</p>
				<p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary, #6b7280)', lineHeight: 1.5 }}>
					{text}
				</p>
			</div>
			<Badge bg={theme.bg} fg={theme.fg}>{score} / 10</Badge>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function SmartScorecard({ data, json, firstName, lastName, }) {
	// Accept either a parsed object or a raw JSON string
	const event = React.useMemo(() => {
		if (data) return data;
		if (json) {
			try {
				return JSON.parse(json);
			} catch {
				return null;
			}
		}
		return null;
	}, [data, json]);

	if (!event?.smart_score) {
		return (
			<p style={{ fontSize: 13, color: 'var(--color-text-danger, #a32d2d)' }}>
				Invalid or missing SmartScore data.
			</p>
		);
	}

	const ss = event.smart_score;
	const det = ss?.score_details ?? {};
	const overall = ss?.overall_fit_assessment_score;
	const overallText = det?.overall_fit_assessment?.text;
	const overallTheme = overall != null ? getScoreTheme(overall, false) : null;
	const borderColor = overall != null ? getSummaryBorderColor(overall) : '#888';

	// Build sub-score list, filtering out nulls
	const subScores = SUB_SCORE_CONFIG
		.map((cfg) => {
			const scoreVal = ss[`${cfg?.key}_score`];
			const detail = det[cfg?.key];
			return { ...cfg, score: scoreVal, text: detail?.text ?? null };
		})
		.filter((item) => item.score != null);

	// Detail rows also require non-empty text
	const detailRows = subScores.filter(
		(item) =>
			item.score !== null &&
			item.score !== undefined &&
			item.text
	);

	return (
		<div
			style={{
				background: 'var(--color-background-primary, #fff)',
				border: '0.5px solid var(--color-border-tertiary, #e2e5ea)',
				borderRadius: 12,
				padding: '1.25rem',
				maxWidth: 850,
			}}
		>
			{/* Header */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
				<div
					style={{
						width: 42, height: 42, borderRadius: '50%',
						background: '#00B2A820',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						fontSize: 14, fontWeight: 400,
						color: '#000000',
						flexShrink: 0,
					}}
				>
					#{String(event?.contact_id).slice(-2)}
				</div>

				<div style={{ flex: 1 }}>
					<p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary, #111)' }}>
						#{event?.contact_id}
						{(firstName || lastName) &&
							`  ${[firstName, lastName].filter(Boolean).join(' ')}`
						}
					</p>
					<p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary, #6b7280)' }}>
						Smart GAP Analysis
					</p>
				</div>

				{overallTheme && (
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
						<div
							style={{
								borderRadius: 10, padding: '8px 16px',
								display: 'flex', flexDirection: 'column', alignItems: 'center',
								background: overallTheme.bg, color: overallTheme.fg,
							}}
						>
							<span style={{ fontSize: 26, fontWeight: 500, lineHeight: 1 }}>{overall}</span>
							<span style={{ fontSize: 10, opacity: 0.75 }}>/ 10 overall</span>
						</div>
						<Badge bg={overallTheme.bg} fg={overallTheme.fg}>{overallTheme.label}</Badge>
					</div>
				)}
			</div>

			{/* Overall fit summary banner */}
			{overallText && (
				<div
					style={{
						background: 'var(--color-background-secondary, #f9fafb)',
						padding: '12px 14px',
						marginBottom: '1rem',
						borderLeft: `3px solid ${borderColor}`,
						borderRadius: '0 8px 8px 0',
						fontSize: 13,
						color: 'var(--color-text-primary, #111)',
						lineHeight: 1.6,
					}}
				>
					<span style={{ marginRight: 6, color: borderColor }}>✦</span>
					{overallText}
				</div>
			)}

			{/* Score tiles */}
			{subScores.length > 0 && (
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
						gap: 8,
						marginBottom: '1rem',
					}}
				>
					{subScores.map((item) => (
						<ScoreTile key={item.key} label={item.label} score={item.score} warn={item.warn} />
					))}
				</div>
			)}

			{/* Detail rows */}
			{detailRows.length > 0 && (
				<div
					style={{
						border: '1px solid #00B2A820',
						borderRadius: 8,
						overflow: 'hidden',
						marginBottom: '1rem',
					}}
				>
					<div
						style={{
							padding: '10px 14px',
							background: '#00B2A808',
							borderBottom: '1px solid #00B2A820',
						}}
					>
						<p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text-secondary, #000000)' }}>
							GAP Details
						</p>
					</div>
					{detailRows.map((item, i) => (
						<DetailRow
							key={item.key}
							label={item.label}
							score={item.score}
							text={item.text}
							warn={item.warn}
							isLast={i === detailRows.length - 1}
						/>
					))}
				</div>
			)}

			{/* Footer */}
			<div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #9ca3af)', textAlign: 'right' }}>
				Hire Intelligence · Smart GAP Analysis #{ss.id} · {formatDate(ss.created)}
			</div>
		</div>
	);
}
